import { useMemo } from 'react';
import { useItems } from '@/hooks/useInventory';
import { isItemCritical } from '@/components/inventory/criticalItemUi';

/**
 * Lookup of inventory item_code → is_critical for Supply Chain line tables
 * that store codes but not the critical flag.
 */
export function useCriticalItemLookup(enabled = true): {
  isCriticalCode: (itemCode?: string | null) => boolean;
  criticalByCode: Map<string, boolean>;
  isLoading: boolean;
} {
  const { data: items = [], isLoading } = useItems({ is_active: true }, { enabled });

  const criticalByCode = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const item of items) {
      const code = String(item.item_code ?? '').trim().toLowerCase();
      if (!code) continue;
      map.set(code, isItemCritical(item));
    }
    return map;
  }, [items]);

  const isCriticalCode = (itemCode?: string | null): boolean => {
    const code = String(itemCode ?? '').trim().toLowerCase();
    if (!code) return false;
    return criticalByCode.get(code) === true;
  };

  return { isCriticalCode, criticalByCode, isLoading };
}

/** Merge is_critical onto lines that only have item_code (or already have the flag). */
export function withCriticalFlag<T extends { item_code?: string | null; is_critical?: boolean | null }>(
  lines: T[],
  isCriticalCode: (itemCode?: string | null) => boolean
): Array<T & { is_critical: boolean }> {
  return lines.map((line) => ({
    ...line,
    is_critical:
      line.is_critical != null ? isItemCritical(line) : isCriticalCode(line.item_code),
  }));
}
